<template>
    <SquareBtn data-component="ConfirmOrderButton"
               data-analytics-id="Order Details / Confirm Order"
               :disabled="!isOnline"
               :loading="confirming"
               @click="confirmOrder">
        <i class="fa-solid fa-check"></i>
        {{ l('ORDER_DETAILS.CONFIRM_ORDER_BTN') }}
    </SquareBtn>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { getLangStringKeyItemTypeVariantsForOrder } from '@/lib/Orders/orderContents';
import { langFilter } from '@/vue/filters/langFilter';
import { mapState } from 'vuex';

export default {
    components: { SquareBtn },

    props: {
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            confirming: false,
        };
    },

    computed: {
        ...mapState(['isOnline']),
    },

    methods: {
        async confirmOrder() {
            // Require permission.
            const result = await container.privilegesManager
                .requirePrivilege(PrivilegeNames.MANUAL_PAYMENT_RECONCILIATION);
            if (!result.granted) {
                // User has no permission.
                return;
            }

            // The implications of confirming the order are shown as a second paragraph in the dialog.
            const itemTypeText = getLangStringKeyItemTypeVariantsForOrder(
                'ORDER_DETAILS.CONFIRM_ORDER_PROMPT',
                this.order
            )
                .map((key) => langFilter(key))
                .join(' ');

            const confirmed = await container.dialogService.confirm(
                'ORDER_DETAILS.CONFIRM_ORDER_PROMPT',
                {
                    cancelColor: 'default',
                    cancelLabel: 'ORDER_DETAILS.CONFIRM_ORDER_PROMPT_CANCEL',
                    customText: itemTypeText || null,
                    okLabel: 'ORDER_DETAILS.CONFIRM_ORDER_BTN',
                }
            );
            if (!confirmed) {
                return;
            }

            this.confirming = true;

            try {
                await container.orderConfirmer.confirm(this.order.ID);
            } catch (error) {
                container.toastFactory.errorTop(
                    'ORDER_DETAILS.CONFIRM_ORDER_FAIL',
                    error.message
                );

                return;
            } finally {
                this.confirming = false;
            }

            // Show the order was successfully confirmed. This is true even if the post-confirm actions fail.
            container.toastFactory.success('ORDER_DETAILS.CONFIRM_ORDER_SUCCESS');

            // The order is now confirmed. The post-confirm action are in a separate try/catch so we can show
            // appropriate error messaging for confirmation failing vs post-confirmation failing.
            // "Confirming" state is re-used to show the spinner again.
            this.confirming = true;

            try {
                await container.orderConfirmer.postConfirm(this.order);
            } catch (error) {
                container.toastFactory.errorTop(
                    'CHECKOUT_ERROR_TITLES.CREATE_INVOICE',
                    error.message
                );
            } finally {
                this.confirming = false;
            }

            // The Angular OrderDetailCtrl listens for this and reloads the order.
            container.eventBus.broadcast(EventNames.ORDER_CONFIRMED, this.order);
        },
    },
};
</script>
